import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './evisio2Companion.html?raw';
import lang from 'dojo/_base/lang';

import _WidgetBaseSP from 'more/_WidgetBaseSP';
import EvisioBridgeHost from '../bridge/EvisioBridgeHost';
import { BRIDGE_TOPIC } from "../bridge/EvisioBridgeTypes";

const IFRAME_LOAD_TIMEOUT_MS = 30000;

class evisio2Companion extends compose(_WidgetBaseSP) {
    templateString: any = template;

    continueAtPath: string = "";

    private mainAppIframe: HTMLIFrameElement;
    private mainAppIframeFallback: HTMLDivElement;
    private mainAppIframeFallbackText: HTMLElement;
    private iframeLoadTimeoutHandle: number | null = null;

    private bridgeHost: EvisioBridgeHost | null = null;

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        let iframeSrc = this._buildIframeSrc(this.continueAtPath || "");

        // Attach the bridge listener BEFORE setting iframe.src so the child's `ready`
        // emission can never race ahead of the parent's listener.
        this.bridgeHost = EvisioBridgeHost.instance(this.mainAppIframe);

        // In-page URL changes flow through the bridge → BRIDGE_TOPIC.UrlChanged →
        // syncParentUrlFromIframe (same single source of truth used by iframe.onload).
        this.addSubscription(BRIDGE_TOPIC.UrlChanged, lang.hitch(this, this.syncParentUrlFromIframe));

        this.mainAppIframe.onload = () => {
            if (this.iframeLoadTimeoutHandle != null){
                clearTimeout(this.iframeLoadTimeoutHandle);
                this.iframeLoadTimeoutHandle = null;
            }
            // Flip bridgeReady false until the new document's client emits ready;
            // any sendCommand in this window will queue and flush on the next ready.
            if (this.bridgeHost) this.bridgeHost.markIframeLoading();
            this.syncParentUrlFromIframe();
        };
        this.mainAppIframe.src = iframeSrc;

        this.iframeLoadTimeoutHandle = window.setTimeout(() => {
            this._showFallback(this.getTranslation("Evisio2_IframeLoadFailed"));
        }, IFRAME_LOAD_TIMEOUT_MS);
    }

    // Exposed for evisio2workspaceRoot, which lifts SDK orchestration above both children.
    // Returns null until postCreate runs (instance is bound there). Lifetime is identical
    // to the companion widget — destroy() nulls it out.
    public getBridgeHost(): EvisioBridgeHost | null {
        return this.bridgeHost;
    }

    destroy(): void {
        if (this.bridgeHost){
            this.bridgeHost.destroy();
            this.bridgeHost = null;
        }
        super.destroy.apply(this, arguments as any);
    }

    private _buildIframeSrc(path: string): string {
        let appRoot: string = (window as any).appRoot || "/";
        let cleanPath = (path || "").replace(/^\/+/, "");
        let baseUrl = appRoot + cleanPath;
        let separator = baseUrl.indexOf("?") >= 0 ? "&" : "?";
        return baseUrl + separator + "inEvisio2=1";
    }

    // Reflects the inner iframe's current location into the parent URL bar.
    // Single source of truth: parent URL is always derived from iframe state, never the other way around.
    // Future bridge-API code that fires on in-iframe pushState/hashchange should call this same method
    // so both paths converge on the same target — no overwrites.
    syncParentUrlFromIframe(): void {
        try {
            let innerLoc = this.mainAppIframe.contentWindow.location;
            let innerPath = innerLoc.pathname || "";
            let innerSearch = innerLoc.search || "";
            let innerHash = innerLoc.hash || "";

            let appRoot: string = (window as any).appRoot || "/";
            let appRootPathOnly = appRoot.replace(/^\/+/, "").replace(/\/+$/, "");

            let cleanPath = innerPath.replace(/^\/+/, "");
            if (appRootPathOnly && cleanPath.indexOf(appRootPathOnly + "/") === 0){
                cleanPath = cleanPath.substring(appRootPathOnly.length + 1);
            } else if (appRootPathOnly && cleanPath === appRootPathOnly){
                cleanPath = "";
            }

            let searchOut = "";
            if (innerSearch){
                let params = new URLSearchParams(innerSearch);
                params.delete("inEvisio2");
                let s = params.toString();
                if (s) searchOut = "?" + s;
            }

            let pathPart = cleanPath ? "Evisio/" + cleanPath : "Evisio";
            let newUrl = appRoot + pathPart + searchOut + innerHash;

            let currentUrl = window.location.pathname + window.location.search + window.location.hash;
            if (newUrl !== currentUrl){
                history.replaceState(null, "", newUrl);
            }
        } catch (e){
            // cross-origin iframe nav or other access error — skip silently
        }
    }

    private _showFallback(message: string): void {
        if (this.iframeLoadTimeoutHandle != null){
            clearTimeout(this.iframeLoadTimeoutHandle);
            this.iframeLoadTimeoutHandle = null;
        }
        this.mainAppIframe.style.display = "none";
        this.mainAppIframeFallbackText.innerHTML = message;
        this.mainAppIframeFallback.classList.remove("w3-hide");
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(evisio2Companion);
